import { Fragment, useSyncExternalStore } from 'react'
import ClickableText from './ClickableText'
import type { FibSegment } from '../../utils/learningUtils'
import { useLocale } from '../../i18n/context'
import {
  contentFontFamily,
  fontMetricsGeneration,
  measureTextWidth,
  subscribeFontMetrics,
} from '../../utils/textMetrics'

export type FibReveal = 'none' | 'masked' | 'full'

/** Weight the 'full' reveal renders the answer at — the blank must reserve
 *  width at the same weight or the two will not agree. */
const REVEAL_FONT_WEIGHT = 600

/**
 * Pre-answer blank, sized to the exact footprint of the text that will replace
 * it on reveal (ADR-014 §2). Previously the width was a font-blind estimate
 * (`len × 13px`, clamped 48–200), which at the ladder's 28px base under-reserves
 * for most words: the sentence gained a line at reveal, after the ADR-027 ladder
 * had settled and could no longer react, and the extra line was clipped at both
 * edges of the container it was measured in.
 *
 * Structure: each word of the answer is an *empty* box of that word's measured
 * width, separated by real spaces, so a multi-word answer keeps exactly the
 * line-break opportunities its revealed text will have. The underline is drawn
 * by the wrapper and runs continuously across those spaces, so the blank still
 * reads as one blank — the word count is not disclosed, and no letter is ever
 * placed in the DOM (widths come from canvas metrics, never from rendered text).
 */
function FibBlank({
  match,
  fontSize,
  label,
}: {
  match: string
  fontSize: number
  label: string
}) {
  // Re-render once if the content webfont resolves after first paint, so the
  // blank never keeps a width measured against the fallback face.
  useSyncExternalStore(subscribeFontMetrics, fontMetricsGeneration, fontMetricsGeneration)

  const font = `${REVEAL_FONT_WEIGHT} ${fontSize}px ${contentFontFamily()}`
  const words = match.split(/\s+/).filter(Boolean)
  const boxes = (words.length > 0 ? words : [match]).map((word) => {
    const measured = measureTextWidth(word, font)
    // Fallback for environments without canvas metrics (jsdom): the pre-fix
    // estimate. Rounding up elsewhere keeps the blank ≥ the revealed text, so
    // any residual error costs a hair of slack rather than a reflowed line.
    return measured === null
      ? Math.max(48, Math.min(200, word.length * 13))
      : Math.ceil(measured)
  })

  return (
    <span
      aria-label={label}
      // The blank is the exercise: Learn measures it (the Definition reveal may
      // not shrink the sentence zone past the blank's line — ADR-100 D7) and
      // the e2e asserts it is still rendered in the revealed state.
      data-testid="fib-blank"
      style={{
        borderBottom: '2.5px solid var(--text-primary)',
        opacity: 0.35,
      }}
    >
      {boxes.map((width, i) => (
        <Fragment key={i}>
          {i > 0 ? ' ' : null}
          <span
            aria-hidden
            style={{
              display: 'inline-block',
              width,
              height: '1em',
              verticalAlign: 'text-bottom',
            }}
          />
        </Fragment>
      ))}
    </span>
  )
}

/**
 * Renders an example sentence tokenized by `buildFillInBlank` into alternating
 * text and blank segments. All blank segments are coordinated by `reveal`:
 *  - 'none'   — every blank renders as a proportional underline placeholder.
 *  - 'masked' — LEGACY first-letter hint ("c______"); retained in the type for
 *               backward compatibility but no longer used at submission time.
 *               Prompt C: both correct and incorrect submissions render 'full'.
 *  - 'full'   — the blank's original surface text (preserves per-occurrence
 *               casing — "Correct" vs "correct"), in `revealColor`. Used for
 *               both correct (green) and incorrect (red) submissions.
 *
 * The user's live typing is intentionally NOT echoed into blanks — only the
 * final correct/wrong state propagates.
 */
export default function FillInBlankSentence({
  segments,
  reveal = 'none',
  revealColor,
  hintLetter,
  speak,
  isSpeechSupported,
  fontSize = 26,
  formHint,
  inlineFormHint = true,
}: {
  segments: FibSegment[]
  reveal?: FibReveal
  revealColor?: string
  /** Soft-reject first-letter hint (ADR-048 §7 amendment). Only applied while
   *  `reveal === 'none'` (soft reject never sets 'full') — the blank keeps its
   *  normal computed width and pre-answer opacity/color; only the glyph
   *  content changes. Pass null/undefined to render the plain blank as today. */
  hintLetter?: string | null
  speak: (word: string) => void
  isSpeechSupported: boolean
  fontSize?: number
  /** Optional grammatical-form hint shown unobtrusively below the sentence
   *  (e.g. "verb, past tense", "noun, plural"). The layout footprint is
   *  reserved whether or not a hint is present — no shift on appear/disappear
   *  per ADR-013/014. See ADR-021. */
  formHint?: string | null
  /** When false the form-hint div is omitted from this component's output so
   *  the caller can render it outside the scroll container (P-L3). Defaults
   *  to true for backward compatibility with PreviousCardOverlay. */
  inlineFormHint?: boolean
}) {
  const { tr } = useLocale()
  return (
    <>
    {/* onPointerDown.preventDefault() blocks the browser from blurring the answer
        input on any tap inside the sentence (ADR-013 keyboard-stays-up invariant).
        onClick on child spans still fires because click comes from pointerup. */}
    <p
      onPointerDown={(e) => e.preventDefault()}
      style={{
        margin: 0,
        fontSize,
        fontFamily: 'var(--font-content)',
        fontWeight: 400,
        color: 'var(--text-primary)',
        textAlign: 'center',
        lineHeight: 1.7,
        wordBreak: 'break-word',
      }}
    >
      {segments.map((seg, i) => {
        if (seg.kind === 'text') {
          return (
            <ClickableText
              key={i}
              text={seg.text}
              speak={speak}
              isSpeechSupported={isSpeechSupported}
            />
          )
        }
        if (reveal === 'none' && !hintLetter) {
          // Plain pre-answer blank, sized to the exact footprint of the reveal.
          // The `marginInline: 4` the old estimate carried is gone: the reveal
          // span has no such margin, and the text segments already supply the
          // sentence's own spaces on both sides of the blank.
          return (
            <FibBlank
              key={i}
              match={seg.match}
              fontSize={fontSize}
              label={tr.learn.blankPlaceholder}
            />
          )
        }
        if (reveal === 'none' && hintLetter) {
          // Soft-reject first-letter hint (ADR-048 §7 amendment). Pixel-
          // alignment fix: this is laid out as if `seg.match` (the exact text
          // the 'full' reveal below will show) were rendered here — same
          // markup shape, same font-weight, same lack of margin/fixed width —
          // with only the first character painted and the rest present but
          // `color: transparent` (reserving their exact glyph widths). This
          // guarantees the first letter's box is pixel-identical to where it
          // will sit once resolved: only per-character color changes on
          // resolution (ghost secondary → revealColor for letter 1, and
          // transparent → revealColor for the rest), never a position shift.
          // The underline is a decoupled absolutely-positioned layer (as in
          // the prior cut of this hint) so its faded opacity never dims the
          // visible letter.
          return (
            <span
              key={i}
              aria-label={tr.learn.blankPlaceholder}
              data-testid="fib-blank"
              style={{ position: 'relative', display: 'inline-block', fontWeight: REVEAL_FONT_WEIGHT }}
            >
              <span aria-hidden style={{
                position: 'absolute', left: 0, right: 0, bottom: 0,
                borderBottom: '2.5px solid var(--text-primary)',
                opacity: 0.35,
              }} />
              <span style={{ color: 'var(--text-secondary)' }}>{seg.match[0]}</span>
              <span aria-hidden style={{ color: 'transparent' }}>{seg.match.slice(1)}</span>
            </span>
          )
        }
        const revealText = reveal === 'full'
          ? seg.match
          : seg.match[0] + '_'.repeat(Math.max(0, seg.match.length - 1))
        return (
          <span
            key={i}
            style={{
              color: revealColor,
              // Coupled to the blank's reserved width — see FibBlank.
              fontWeight: REVEAL_FONT_WEIGHT,
              borderBottom: `2px solid ${revealColor}`,
              paddingBottom: 1,
            }}
          >
            {revealText}
          </span>
        )
      })}
    </p>
    {/* Form hint — rendered inline only when inlineFormHint=true (default).
        Pass inlineFormHint={false} to render the hint outside a scroll
        container instead (P-L3). Reserved-height slot guarantees no layout
        shift on appear/disappear per ADR-013/014/021. */}
    {inlineFormHint && (
    <div
      data-testid="form-hint-slot"
      aria-hidden={!formHint}
      style={{
        minHeight: 18,
        marginTop: 8,
        textAlign: 'center',
        fontSize: 12,
        fontStyle: 'italic',
        color: 'var(--text-secondary)',
        opacity: formHint ? 0.85 : 0,
        letterSpacing: '0.02em',
        lineHeight: 1.4,
      }}
    >
      {formHint ?? ' '}
    </div>
    )}
    </>
  )
}
